import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { OptimizedImage } from "@shared/components/OptimizedImage";
import {
  productCardQuantityResolver,
  type ProductCardQuantityForm,
} from "@shared/schemas";
import { useCartStore } from "@public/stores/cartStore";
import type { Product } from "@shared/types/product";

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product, quantity: number) => void;
  showQuantitySelector?: boolean;
  className?: string;
  imageClassName?: string;
  contentClassName?: string;
  variant?: "default" | "compact" | "large" | "grid" | "modern";
}

export function ProductCard({
  product,
  onAddToCart,
  showQuantitySelector = true,
  className = "",
  imageClassName = "",
  contentClassName = "",
  variant = "default",
}: ProductCardProps) {
  // React Hook Form con Zod
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
    reset,
  } = useForm<ProductCardQuantityForm>({
    resolver: productCardQuantityResolver.resolver,
    defaultValues: productCardQuantityResolver.defaultValues,
    mode: productCardQuantityResolver.mode,
  });

  const quantity = watch("quantity");

  const handleQuantityChange = (newQuantity: number) => {
    setValue("quantity", newQuantity, { shouldValidate: true });
  };

  const addToCart = useCartStore((state) => state.addToCart);

  const onSubmit = (data: ProductCardQuantityForm) => {
    if (data.quantity > 0) {
      if (onAddToCart) {
        onAddToCart(product, data.quantity);
      } else {
        addToCart(product, data.quantity);
      }
      reset(); // Reset form after adding
    }
  };

  const defaultImage =
    "https://res.cloudinary.com/dq8ig7aly/image/upload/v1749660976/no-image_lbt2of.jpg";
  const productImage =
    product.images && product.images.length > 0
      ? product.images[0].url
      : defaultImage;

  // Configuraciones por variante
  const variantConfig = {
    default: {
      imageHeight: "h-48",
      contentSpacing: "space-y-3",
      titleSize: "text-base",
      descriptionSize: "text-sm",
      showDescription: true,
    },
    compact: {
      imageHeight: "h-32",
      contentSpacing: "space-y-2",
      titleSize: "text-sm",
      descriptionSize: "text-xs",
      showDescription: false,
    },
    large: {
      imageHeight: "h-64",
      contentSpacing: "space-y-4",
      titleSize: "text-lg",
      descriptionSize: "text-base",
      showDescription: true,
    },
    grid: {
      imageHeight: "h-32",
      contentSpacing: "space-y-2",
      titleSize: "text-sm",
      descriptionSize: "text-xs",
      showDescription: true,
    },
    modern: {
      imageHeight: "aspect-square",
      contentSpacing: "",
      titleSize: "text-base",
      descriptionSize: "text-sm",
      showDescription: false,
    },
  };

  const config = variantConfig[variant];

  // Generar URL del producto a partir del slug
  const productUrl = `/producto/${product.slug}`;

  if (variant === "modern") {
    return (
      <article
        className={`flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md ${className}`}
      >
        <Link
          to={productUrl}
          state={{ product }}
          className={`relative block ${config.imageHeight} overflow-hidden ${imageClassName}`}
        >
          <OptimizedImage
            src={productImage}
            alt={product.name}
            width={480}
            height={480}
            quality={90}
            format="webp"
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
          />
        </Link>

        <div className={`flex flex-1 flex-col gap-3 p-4 ${contentClassName}`}>
          {product.brand && (
            <p className="text-xs uppercase tracking-wide text-gray-400">
              {product.brand}
            </p>
          )}

          <Link
            to={productUrl}
            state={{ product }}
            className="line-clamp-2 font-semibold text-gray-900 hover:text-[#282828]"
          >
            {product.name}
          </Link>

          <p className="text-xs uppercase tracking-wide text-gray-300">
            {product.slug}
          </p>

          <div className="mt-auto">
            <Link
              to={productUrl}
              state={{ product }}
              className="inline-flex w-full items-center justify-center rounded-lg bg-[#282828] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#1a1a1a]"
            >
              Ver producto
            </Link>
          </div>
        </div>
      </article>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 ${
        variant === "grid" ? "p-3" : "p-6"
      } flex flex-col group ${className}`}
    >
      {/* Imagen del producto - Clickeable para navegar */}
      <Link
        to={productUrl}
        state={{ product }}
        className={`${config.imageHeight} bg-gray-200 rounded-lg ${
          variant === "grid" ? "mb-3" : "mb-4"
        } overflow-hidden block ${imageClassName}`}
      >
        <OptimizedImage
          src={productImage}
          alt={product.name}
          width={400}
          height={300}
          quality={90}
          format="webp"
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200"
        />
      </Link>

      {/* Información del producto */}
      <div className={`flex-1 ${config.contentSpacing} ${contentClassName}`}>
        {/* Marca/Fabricante */}
        <p
          className={`text-gray-500 font-medium ${
            variant === "grid" ? "text-sm" : "text-sm"
          }`}
        >
          {product.brand}
        </p>

        {/* Nombre del producto - Clickeable para navegar */}
        <Link
          to={productUrl}
          state={{ product }}
          className={`font-semibold text-gray-900 ${config.titleSize} leading-tight line-clamp-2 hover:text-blue-600 transition-colors block`}
        >
          {product.name}
        </Link>

        {/* Descripción (opcional, solo si hay espacio) */}
        {config.showDescription && product.description && (
          <p className={`text-gray-600 line-clamp-2 ${config.descriptionSize}`}>
            {product.description}
          </p>
        )}

        {/* Selector de cantidad */}
        {showQuantitySelector && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              className={`flex items-center justify-center space-x-2 ${
                variant === "grid" ? "py-2" : "py-2"
              }`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(quantity - 1);
                }}
                disabled={quantity <= 0}
                className={`rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  variant === "grid" ? "w-7 h-7" : "w-8 h-8"
                }`}
              >
                <span
                  className={`font-bold ${
                    variant === "grid" ? "text-sm" : "text-sm"
                  }`}
                >
                  -
                </span>
              </button>

              <input
                type="number"
                {...register("quantity", { valueAsNumber: true })}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                min="0"
                max="10000"
                className={`text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  variant === "grid" ? "w-14 h-7 text-sm" : "w-16 h-8 text-sm"
                }`}
              />

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(quantity + 1);
                }}
                className={`rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-gray-700 transition-colors ${
                  variant === "grid" ? "w-7 h-7" : "w-8 h-8"
                }`}
              >
                <span
                  className={`font-bold ${
                    variant === "grid" ? "text-sm" : "text-sm"
                  }`}
                >
                  +
                </span>
              </button>
            </div>

            {/* Botón AGREGAR */}
            <button
              type="submit"
              onClick={(e) => e.stopPropagation()}
              disabled={quantity <= 0 || !isValid || isNaN(quantity)}
              className={`w-full bg-gray-800 text-white font-medium rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors uppercase ${
                variant === "grid" ? "py-2 px-3 text-sm" : "py-2 px-4 text-sm"
              }`}
            >
              Agregar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
